import { cn } from '../../lib/utils';

export type MaturityStage = 'alpha' | 'beta' | 'preview';
export type MaturityBadgeSize = 'default' | 'compact' | 'pill';

interface MaturityBadgeProps {
  stage: MaturityStage;
  /** `compact` trims padding and text for dense contexts like sidebar nav items; `pill` is a softly-rounded marker for hero/brand contexts like the auth wordmark. */
  size?: MaturityBadgeSize;
  className?: string;
}

const stageConfig = {
  alpha: {
    label: 'Alpha',
    defaultClass: [
      'bg-amber-400/8 text-amber-700/60 border-amber-500/20',
      'dark:bg-amber-400/8 dark:text-amber-400/50 dark:border-amber-400/15',
    ].join(' '),
    // Muted, top-lit amber for the `pill` brand chip — present but understated.
    // Light mode needs a darker fill/text so the chip reads on a pale header;
    // the dark: overrides preserve the original dim-amber-on-dark treatment.
    pillClass: [
      'border-amber-500/30 from-amber-300/40 to-amber-500/15 text-amber-800/80',
      'dark:border-amber-400/20 dark:from-amber-200/15 dark:to-amber-400/[0.06] dark:text-amber-200/70',
    ].join(' '),
  },
  beta: {
    label: 'Beta',
    defaultClass: [
      'bg-violet-400/8 text-violet-700/60 border-violet-500/20',
      'dark:bg-violet-400/8 dark:text-violet-400/50 dark:border-violet-400/15',
    ].join(' '),
    pillClass: [
      'border-violet-500/30 from-violet-300/40 to-violet-500/15 text-violet-800/80',
      'dark:border-violet-400/30 dark:from-violet-200/25 dark:to-violet-400/10 dark:text-violet-200',
    ].join(' '),
  },
  preview: {
    label: 'Preview',
    // Neutral — product-level marker, no hue to avoid alarm/celebration reads
    defaultClass: [
      'bg-foreground/5 text-foreground/45 border-foreground/15',
      'dark:bg-foreground/5 dark:text-foreground/40 dark:border-foreground/10',
    ].join(' '),
    pillClass: 'border-foreground/20 from-foreground/15 to-foreground/5 text-foreground/80',
  },
} satisfies Record<MaturityStage, { label: string; defaultClass: string; pillClass: string }>;

// Compact nav variant is deliberately colorless — no border, no hue, just
// a faint annotation that doesn't compete with the nav label itself.
const COMPACT_CLASS =
  'bg-transparent border-transparent text-muted-foreground/35 dark:text-muted-foreground/25';

const sizeConfig: Record<MaturityBadgeSize, string> = {
  default: 'px-1.5 py-px text-[10px] tracking-wider border rounded',
  compact: 'px-0.5 py-px text-[9px] tracking-wide border',
  // Crafted brand chip for hero/header contexts: a soft top-lit gradient fill
  // (hue supplied per-stage via `pillClass`), crisp border, and an inset top
  // highlight so it catches light instead of reading as a flat tint. The
  // `text-indent` cancels the trailing letter-spacing so the uppercase label
  // stays optically centered horizontally; the asymmetric `pt-0.5 pb-0` (same
  // total height as `py-px`) nudges the cap-height-only glyphs down so they sit
  // optically centered vertically rather than reading slightly high.
  pill: 'rounded-full border bg-gradient-to-b px-2 pt-0.5 pb-0 text-[9px] font-semibold tracking-[0.14em] [text-indent:0.14em] shadow-[inset_0_1px_0_rgba(255,255,255,0.14),0_1px_2px_rgba(0,0,0,0.25)] backdrop-blur-md',
};

/**
 * Subtle inline marker for features that are not yet generally available.
 * Renders as a whisper-quiet tag — intentionally below the visual weight of a
 * status badge. Place directly after a title or nav label.
 *
 * Use `size="compact"` in dense contexts like sidebar nav items — it drops
 * the border and color entirely, reading as a faint annotation rather than a
 * badge. Use `size="pill"` for brand/header contexts (the auth wordmark, the
 * sidebar header) where the marker should read as a crafted, self-contained
 * chip — it carries the same look everywhere so the stage marker stays
 * consistent across surfaces.
 */
export function MaturityBadge({ stage, size = 'default', className }: MaturityBadgeProps) {
  const { label, defaultClass, pillClass } = stageConfig[stage];
  const colorClass =
    size === 'compact' ? COMPACT_CLASS : size === 'pill' ? pillClass : defaultClass;

  return (
    <span
      data-testid="maturity-badge"
      data-stage={stage}
      className={cn(
        'inline-flex items-center font-medium uppercase leading-none select-none',
        sizeConfig[size],
        colorClass,
        className,
      )}
    >
      {label}
    </span>
  );
}
